/**
 * Video Zoom Utility
 * Handles zooming functionality for video streams using camera zoom when available,
 * with fallback to digital zoom (canvas manipulation)
 */

const POSITIONED_VALUES = ['relative', 'absolute', 'fixed']

function ensurePositioned (el) {
  if (!el) return
  const pos = window.getComputedStyle(el).position
  if (!POSITIONED_VALUES.includes(pos)) {
    el.classList.add('video-zoom-container--relative')
  }
}

class VideoZoom {
  constructor () {
    // Unique identifier for this zoom instance
    this.instanceId = Math.random().toString(36).substr(2, 9)

    // Session-specific lock to prevent cross-user interference
    this.sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
    this.isLocked = false
    this.lockTimeout = null
    this.lastOperationTime = 0

    this.zoomLevel = 1.0
    this.maxZoom = 3.0
    this.minZoom = 1.0
    this.zoomStep = 0.2
    this.isZoomed = false
    this.zoomCenter = { x: 0.5, y: 0.5 } // Center of zoom (0-1 coordinates)

    // Camera zoom support
    this.supportsCameraZoom = false
    this.cameraZoomCapabilities = null
    this.currentVideoTrack = null

    // Canvas elements (for digital zoom fallback)
    this.sourceCanvas = null
    this.outputCanvas = null
    this.sourceCtx = null
    this.outputCtx = null

    // MediaStream handling
    this.originalStream = null
    this.zoomedStream = null
    this.videoElement = null

    // Animation frame
    this.animationId = null
    this.isProcessing = false

    // Zoom mode
    this.zoomMode = 'camera' // 'camera' or 'digital'

    // Pinch zoom state
    this.pinchInitialZoom = null

    console.log(`[VideoZoom] New instance created: ${this.instanceId} (Session: ${this.sessionId})`)
  }

  /**
   * Initialize zoom functionality with a video element
   * @param {HTMLVideoElement} videoElement - The video element to zoom
   * @param {MediaStream} stream - The original media stream
   */
  initialize (videoElement, stream) {
    // Additional safety checks
    if (!videoElement || !stream) {
      console.warn('[VideoZoom] Cannot initialize: missing video element or stream')
      return false
    }

    // Check if video element is ready
    if (videoElement.readyState === 0) {
      console.warn('[VideoZoom] Video element not ready (readyState = 0), waiting...')
      // Wait for video to be ready before initializing
      videoElement.addEventListener('loadeddata', () => {
        this.initialize(videoElement, stream)
      }, { once: true })
      return false
    }

    // Enhanced container validation for direct product mode
    const videoContainer = videoElement.parentNode
    if (!videoContainer) {
      console.warn('[VideoZoom] Cannot initialize: video element has no parent container')
      return false
    }

    // Check if this is a valid container for zoom
    const isValidContainer = this.isValidZoomContainer(videoContainer)
    if (!isValidContainer) {
      console.warn('[VideoZoom] Cannot initialize: video element is not in a valid zoom container')
      return false
    }

    this.videoElement = videoElement
    this.originalStream = stream

    // Check camera zoom capabilities
    this.checkCameraZoomSupport()

    if (this.supportsCameraZoom) {
      // Use camera zoom
      this.zoomMode = 'camera'
      this.initializeCameraZoom()
      console.log('Camera zoom initialized - using hardware zoom')
    } else {
      // Fallback to digital zoom
      this.zoomMode = 'digital'
      this.initializeDigitalZoom()
      console.log('Digital zoom initialized - camera zoom not supported')

      // Emit event for UI to show popup
      this.onDeviceNotSupported && this.onDeviceNotSupported()
    }

    return true
  }

  /**
   * Check if the container is valid for zoom functionality
   * @param {HTMLElement} container - The container element to check
   * @returns {boolean} True if the container is valid for zoom
   */
  isValidZoomContainer (container) {
    console.log('[VideoZoom] isValidZoomContainer called with:', container)

    if (!container) {
      console.log('[VideoZoom] Container is missing')
      return false
    }

    const containerId = container.id || ''
    const containerClasses = container.className || ''

    // For direct product mode, we need to handle both Local and Remote containers
    // since the container IDs are dynamically assigned based on user type and video direction
    // Also handle containers without IDs but with the correct classes
    if (containerId.includes('Local') || containerId.includes('Remote') || containerClasses.includes('webrtc-remote-video-container')) {
      // Ensure this is actually a local video stream
      // Check if the video element has a local stream or if it's the user's own video
      let videoElement = container.querySelector('video#local')
      if (!videoElement) {
        videoElement = container.querySelector('video')
      }
      console.log('[VideoZoom] Video element found:', videoElement)

      if (videoElement && videoElement.srcObject) {
        // Check if this is a local stream by looking at the track's kind and readyState
        const tracks = videoElement.srcObject.getTracks()
        const videoTrack = tracks.find(track => track.kind === 'video')
        console.log('[VideoZoom] Video track:', videoTrack)

        if (videoTrack && videoTrack.readyState === 'live') {
          console.log('[VideoZoom] Video track is live - container is valid')
          // Allow zoom on any live video track that appears to be local
          // But exclude remote videos (they have class 'remote_video')
          if (!videoElement.classList.contains('remote_video')) {
            console.log('[VideoZoom] Video is not remote - container is valid for zoom')
            return true
          }
        }
      }
    }

    // Fallback to original logic for non-direct products
    const fallbackResult = containerId.includes('Local') || containerClasses.includes('webrtc-remote-video-container')
    console.log('[VideoZoom] Fallback result:', fallbackResult)
    return fallbackResult
  }

  /**
   * Check if the device supports camera zoom
   */
  checkCameraZoomSupport () {
    if (!this.originalStream) {
      this.supportsCameraZoom = false
      return
    }

    const videoTracks = this.originalStream.getVideoTracks()
    if (videoTracks.length === 0) {
      this.supportsCameraZoom = false
      return
    }

    this.currentVideoTrack = videoTracks[0]

    try {
      // Check if zoom is supported
      const capabilities = this.currentVideoTrack.getCapabilities()
      this.cameraZoomCapabilities = capabilities.zoom || null

      if (this.cameraZoomCapabilities) {
        this.supportsCameraZoom = true
        this.minZoom = this.cameraZoomCapabilities.min || 1.0
        this.maxZoom = this.cameraZoomCapabilities.max || 3.0
        console.log(`Camera zoom supported: ${this.minZoom}x to ${this.maxZoom}x`)
      } else {
        this.supportsCameraZoom = false
        console.log('Camera zoom not supported by this device')
      }
    } catch (error) {
      console.warn('Error checking camera zoom capabilities:', error)
      this.supportsCameraZoom = false
    }
  }

  /**
   * Initialize camera zoom functionality
   */
  initializeCameraZoom () {
    // For camera zoom, we don't need canvas processing
    // The zoom is handled directly by the camera hardware
    this.zoomedStream = this.originalStream

    // Apply zoom effect to local video element display
    this.applyLocalZoomEffect()
  }

  /**
   * Initialize digital zoom functionality (fallback)
   */
  initializeDigitalZoom () {
    // Additional safety checks
    if (!this.videoElement) {
      console.error('[VideoZoom] Cannot initialize digital zoom: no video element')
      return
    }

    // Enhanced container validation for direct product mode
    const videoContainer = this.videoElement.parentNode
    if (!videoContainer) {
      console.warn('[VideoZoom] Cannot initialize digital zoom: video element has no parent container')
      return
    }

    // Check if this is a valid container for zoom
    const isValidContainer = this.isValidZoomContainer(videoContainer)
    if (!isValidContainer) {
      console.warn('[VideoZoom] Cannot initialize digital zoom: video element is not in a valid zoom container')
      return
    }

    // Don't interfere if video is not ready
    if (this.videoElement.readyState === 0) {
      console.warn('[VideoZoom] Video element not ready, skipping digital zoom initialization')
      return
    }

    // Create source canvas for capturing video frames
    this.sourceCanvas = document.createElement('canvas')
    this.sourceCtx = this.sourceCanvas.getContext('2d')

    // Create output canvas for zoomed video
    this.outputCanvas = document.createElement('canvas')
    this.outputCtx = this.outputCanvas.getContext('2d')

    // Set initial canvas dimensions
    this.updateCanvasDimensions()

    // Insert output canvas into DOM (positioned over the video element)
    if (this.videoElement && this.videoElement.parentNode) {
      this.outputCanvas.classList.add('video-zoom-output-canvas')

      const videoContainer = this.videoElement.parentNode
      ensurePositioned(videoContainer)
      ensurePositioned(this.videoElement)

      // Insert canvas after the video element
      videoContainer.insertBefore(this.outputCanvas, this.videoElement.nextSibling)
    } else {
      console.error('[VideoZoom] Cannot insert canvas: video element or parent not found')
    }

    // Create zoomed stream from output canvas
    this.zoomedStream = this.outputCanvas.captureStream(30) // 30 FPS

    // Apply zoom effect to local video element immediately
    this.applyLocalZoomEffect()

    // Wait for video to be ready before starting processing
    this.waitForVideoReady()
  }

  /**
   * Apply zoom effect to local video display
   */
  applyLocalZoomEffect () {
    console.log('[VideoZoom] applyLocalZoomEffect called with zoomLevel:', this.zoomLevel)

    if (!this.videoElement) {
      console.log('[VideoZoom] No video element found')
      return
    }

    // Enhanced container validation for direct product mode
    const videoContainer = this.videoElement.parentNode
    if (!videoContainer) {
      console.warn('[VideoZoom] Cannot apply zoom effect: video element has no parent container')
      return
    }

    // Check if this is a valid container for zoom
    const isValidContainer = this.isValidZoomContainer(videoContainer)
    if (!isValidContainer) {
      console.warn('[VideoZoom] Cannot apply zoom effect: video element is not in a valid zoom container')
      return
    }

    // Don't interfere if video is not ready
    if (this.videoElement.readyState === 0) {
      console.warn('[VideoZoom] Video element not ready, skipping zoom effect application')
      return
    }

    if (this.zoomLevel === 1.0) {
      console.log('[VideoZoom] Resetting zoom effect - showing original video')
      this.videoElement.classList.remove('video-zoom-video--active')
      this.videoElement.classList.add('video-zoom-video--idle')

      if (this.outputCanvas) {
        this.outputCanvas.classList.remove('is-visible')
        console.log('[VideoZoom] Canvas hidden')
      }
    } else {
      console.log('[VideoZoom] Applying zoom effect - showing canvas with zoom level:', this.zoomLevel)
      this.videoElement.classList.remove('video-zoom-video--idle')
      this.videoElement.classList.add('video-zoom-video--active')

      if (this.outputCanvas) {
        this.outputCanvas.classList.add('is-visible')
      }
    }
  }

  /**
   * Wait for video to be ready before starting processing
   */
  waitForVideoReady () {
    if (this.videoElement.readyState >= 2) {
      // Video is ready, start processing
      setTimeout(() => {
        this.startProcessing()
      }, 500)
    } else {
      // Wait for video to be ready
      this.videoElement.addEventListener('loadeddata', () => {
        setTimeout(() => {
          this.startProcessing()
        }, 500)
      }, { once: true })

      // Fallback timeout
      setTimeout(() => {
        if (!this.isProcessing) {
          this.startProcessing()
        }
      }, 2000)
    }
  }

  /**
   * Update canvas dimensions to match video
   */
  updateCanvasDimensions () {
    if (!this.videoElement) return

    const width = this.videoElement.videoWidth || this.videoElement.clientWidth || 640
    const height = this.videoElement.videoHeight || this.videoElement.clientHeight || 480

    // Ensure minimum dimensions
    const minWidth = 320
    const minHeight = 240

    const finalWidth = Math.max(width, minWidth)
    const finalHeight = Math.max(height, minHeight)

    // Only update if dimensions actually changed
    if (this.sourceCanvas.width !== finalWidth || this.sourceCanvas.height !== finalHeight) {
      this.sourceCanvas.width = finalWidth
      this.sourceCanvas.height = finalHeight
      this.outputCanvas.width = finalWidth
      this.outputCanvas.height = finalHeight
    }
  }

  /**
   * Start processing video frames
   */
  startProcessing () {
    if (this.isProcessing) return

    this.isProcessing = true
    this.processFrame()
  }

  /**
   * Stop processing video frames
   */
  stopProcessing () {
    this.isProcessing = false
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  /**
   * Process a single video frame
   */
  processFrame () {
    if (!this.isProcessing || !this.videoElement || !this.sourceCtx || !this.outputCtx) {
      console.log('[VideoZoom] processFrame skipped - conditions not met:')
      return
    }

    try {
      // Check if video has valid dimensions
      const videoWidth = this.videoElement.videoWidth || this.videoElement.clientWidth
      const videoHeight = this.videoElement.videoHeight || this.videoElement.clientHeight

      if (!videoWidth || !videoHeight || videoWidth === 0 || videoHeight === 0) {
        // Video not ready yet, try again next frame
        this.animationId = requestAnimationFrame(() => this.processFrame())
        return
      }

      // Update canvas dimensions if they don't match video
      if (this.sourceCanvas.width !== videoWidth || this.sourceCanvas.height !== videoHeight) {
        this.updateCanvasDimensions()
      }

      // Check if video is actually playing
      if (this.videoElement.readyState < 2) {
        // Video not ready, try again next frame
        this.animationId = requestAnimationFrame(() => this.processFrame())
        return
      }

      // Draw the current video frame to source canvas
      this.sourceCtx.drawImage(this.videoElement, 0, 0, this.sourceCanvas.width, this.sourceCanvas.height)

      // Clear output canvas
      this.outputCtx.clearRect(0, 0, this.outputCanvas.width, this.outputCanvas.height)

      // Apply zoom transformation
      this.applyZoom()

      // Add frame counter for debugging
      if (!this.frameCounter) this.frameCounter = 0
      this.frameCounter++

      // Continue processing
      this.animationId = requestAnimationFrame(() => this.processFrame())
    } catch (error) {
      console.error('Error processing video frame:', error)
      // Continue processing even if there's an error
      this.animationId = requestAnimationFrame(() => this.processFrame())
    }
  }

  /**
   * Apply zoom transformation to the video
   */
  applyZoom () {
    try {
      if (this.zoomLevel === 1.0) {
        // No zoom, just copy the frame
        this.outputCtx.drawImage(this.sourceCanvas, 0, 0)
        return
      }

      const canvas = this.outputCanvas
      const ctx = this.outputCtx

      // Validate canvas dimensions
      if (!canvas.width || !canvas.height || canvas.width === 0 || canvas.height === 0) {
        console.warn('[VideoZoom] Canvas has invalid dimensions, skipping zoom')
        return
      }

      // Calculate zoom dimensions
      const zoomWidth = canvas.width / this.zoomLevel
      const zoomHeight = canvas.height / this.zoomLevel

      // Calculate source rectangle (what part of the original video to show)
      const sourceX = (canvas.width - zoomWidth) * this.zoomCenter.x
      const sourceY = (canvas.height - zoomHeight) * this.zoomCenter.y

      // Draw zoomed portion
      ctx.drawImage(
        this.sourceCanvas,
        sourceX, sourceY, zoomWidth, zoomHeight, // Source rectangle
        0, 0, canvas.width, canvas.height // Destination rectangle
      )
    } catch (error) {
      console.error('Error applying zoom:', error)
    }
  }

  /**
   * Set zoom level
   * @param {number} level - Zoom level (1.0 = no zoom, 2.0 = 2x zoom, etc.)
   */
  async setZoomLevel (level) {
    // Ensure zoom level is within bounds and properly isolated
    const clampedLevel = Math.max(this.minZoom, Math.min(this.maxZoom, level))

    // Only update if the level actually changed significantly
    if (Math.abs(this.zoomLevel - clampedLevel) < 0.01) {
      return
    }

    this.zoomLevel = clampedLevel
    this.isZoomed = this.zoomLevel > 1.01 // Use small threshold to avoid floating point issues

    // Apply zoom effect to local video display
    this.applyLocalZoomEffect()

    if (this.zoomMode === 'camera') {
      // Check if track is still valid before applying camera zoom
      if (this.currentVideoTrack && this.currentVideoTrack.readyState === 'live') {
        await this.applyCameraZoom()
      } else {
        console.warn('[VideoZoom] Video track not valid, falling back to digital zoom')
        this.fallbackToDigitalZoom()
      }
    }

    // Emit zoom change event
    if (this.onZoomChange) {
      this.onZoomChange(this.zoomLevel, this.isZoomed)
    }
  }

  /**
   * Check if zoom operation is allowed (rate limiting and session-based)
   * @returns {boolean} True if operation is allowed
   */
  isOperationAllowed () {
    const now = Date.now()
    const timeSinceLastOperation = now - this.lastOperationTime

    // Rate limiting: minimum 200ms between operations
    if (timeSinceLastOperation < 200) {
      console.log(`[VideoZoom:${this.instanceId}] Operation blocked - rate limiting (${timeSinceLastOperation}ms since last operation)`)
      return false
    }

    // Check if locked
    if (this.isLocked) {
      console.log(`[VideoZoom:${this.instanceId}] Operation blocked - instance is locked`)
      return false
    }

    return true
  }

  /**
   * Lock zoom operations temporarily to prevent rapid changes
   * @param {number} duration - Lock duration in milliseconds
   */
  lockOperations (duration = 300) {
    this.isLocked = true
    clearTimeout(this.lockTimeout)
    this.lockTimeout = setTimeout(() => {
      this.isLocked = false
      console.log(`[VideoZoom:${this.instanceId}] Operations unlocked`)
    }, duration)
  }

  /**
   * Zoom in - session-based isolation
   */
  zoomIn () {
    if (!this.isOperationAllowed()) {
      return
    }

    const newZoom = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom)

    // Update last operation time
    this.lastOperationTime = Date.now()

    // Lock operations temporarily
    this.lockOperations(300)

    this.setZoomLevel(newZoom)
  }

  /**
   * Zoom out - session-based isolation
   */
  zoomOut () {
    if (!this.isOperationAllowed()) {
      return
    }

    const newZoom = Math.max(this.zoomLevel - this.zoomStep, this.minZoom)

    // Update last operation time
    this.lastOperationTime = Date.now()

    // Lock operations temporarily
    this.lockOperations(300)

    this.setZoomLevel(newZoom)
  }

  /**
   * Apply camera zoom using hardware
   */
  async applyCameraZoom () {
    if (!this.supportsCameraZoom || !this.currentVideoTrack) {
      return
    }

    // Check if the track is still valid and active
    if (this.currentVideoTrack.readyState !== 'live') {
      console.warn('[VideoZoom] Video track is not in live state, falling back to digital zoom')
      this.fallbackToDigitalZoom()
      return
    }

    // Check if the track is still part of an active stream
    if (!this.originalStream || !this.originalStream.getVideoTracks().includes(this.currentVideoTrack)) {
      console.warn('[VideoZoom] Video track is no longer part of the stream, falling back to digital zoom')
      this.fallbackToDigitalZoom()
      return
    }

    try {
      await this.currentVideoTrack.applyConstraints({
        advanced: [{ zoom: this.zoomLevel }]
      })
      console.log(`Camera zoom applied: ${this.zoomLevel}x`)
    } catch (error) {
      console.error('Error applying camera zoom:', error)
      // Fallback to digital zoom if camera zoom fails
      this.fallbackToDigitalZoom()
    }
  }

  /**
   * Fallback to digital zoom if camera zoom fails
   */
  fallbackToDigitalZoom () {
    console.log('Falling back to digital zoom')
    this.zoomMode = 'digital'
    this.supportsCameraZoom = false
    this.initializeDigitalZoom()

    // Emit event for UI to show popup
    this.onFallbackToDigital && this.onFallbackToDigital()
  }

  /**
   * Set zoom center point
   * @param {number} x - X coordinate (0-1)
   * @param {number} y - Y coordinate (0-1)
   */
  setZoomCenter (x, y) {
    this.zoomCenter.x = Math.max(0, Math.min(1, x))
    this.zoomCenter.y = Math.max(0, Math.min(1, y))

    // Update local zoom effect when center changes
    this.applyLocalZoomEffect()
  }

  /**
   * Check if camera zoom is supported
   * @returns {boolean} True if camera zoom is supported
   */
  isCameraZoomSupported () {
    return this.supportsCameraZoom
  }

  /**
   * Get the zoomed stream
   * @returns {MediaStream} The zoomed media stream
   */
  getZoomedStream () {
    return this.zoomedStream
  }

  /**
   * Update the video track reference (called when camera is switched)
   * @param {MediaStreamTrack} newVideoTrack - The new video track
   */
  updateVideoTrack (newVideoTrack) {
    if (newVideoTrack && newVideoTrack.kind === 'video') {
      this.currentVideoTrack = newVideoTrack

      // Re-check camera zoom support for the new track
      this.checkCameraZoomSupport()

      // If we were using camera zoom and the new track doesn't support it, fallback to digital
      if (this.zoomMode === 'camera' && !this.supportsCameraZoom) {
        console.log('[VideoZoom] New video track does not support camera zoom, switching to digital zoom')
        this.fallbackToDigitalZoom()
      }
    }
  }

  /**
   * Handle pinch-to-zoom gesture
   * @param {number} scale - Pinch scale factor
   * @param {number} centerX - Center X coordinate (0-1)
   * @param {number} centerY - Center Y coordinate (0-1)
   */
  handlePinchZoom (scale, centerX, centerY) {
    // Store initial zoom level on first pinch if not already stored
    if (!this.pinchInitialZoom) {
      this.pinchInitialZoom = this.zoomLevel
      console.log('[VideoZoom] Pinch started with initial zoom:', this.pinchInitialZoom)
    }

    // Calculate new zoom level based on initial zoom and pinch scale
    // This prevents the zoom from jumping beyond limits immediately
    const rawZoom = this.pinchInitialZoom * scale
    const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, rawZoom))

    // Only update if the change is significant (prevents micro-adjustments)
    // Use a smaller threshold for pinch gestures to make them more responsive
    if (Math.abs(newZoom - this.zoomLevel) > 0.02) {
      this.setZoomLevel(newZoom)
      this.setZoomCenter(centerX, centerY)
    }
  }

  /**
   * Reset pinch zoom state when gesture ends
   */
  resetPinchZoomState () {
    this.pinchInitialZoom = null
  }

  /**
   * Get current zoom level
   * @returns {number} Current zoom level
   */
  getCurrentZoomLevel () {
    return this.zoomLevel
  }

  /**
   * Check if zoom is working properly
   * @returns {boolean} True if zoom is working
   */
  isZoomWorking () {
    if (this.zoomMode === 'camera') {
      return this.supportsCameraZoom && this.currentVideoTrack && this.currentVideoTrack.readyState === 'live'
    } else {
      return this.isProcessing && this.sourceCanvas && this.outputCanvas &&
             this.sourceCanvas.width > 0 && this.outputCanvas.width > 0
    }
  }

  /**
   * Get zoom status information
   * @returns {Object} Zoom status information
   */
  getZoomStatus () {
    return {
      instanceId: this.instanceId,
      mode: this.zoomMode,
      level: this.zoomLevel,
      isZoomed: this.isZoomed,
      isWorking: this.isZoomWorking(),
      supportsCameraZoom: this.supportsCameraZoom,
      isProcessing: this.isProcessing
    }
  }

  /**
   * Clean up resources
   */
  destroy () {
    console.log('[VideoZoom] Destroying video zoom instance')

    // Stop processing
    this.stopProcessing()

    // Reset camera zoom if active
    if (this.zoomMode === 'camera' && this.currentVideoTrack) {
      this.currentVideoTrack.applyConstraints({
        advanced: [{ zoom: 1.0 }]
      }).catch(err => console.warn('Error resetting camera zoom:', err))
    }

    if (this.outputCanvas) {
      this.outputCanvas.classList.remove('is-visible')
      if (this.outputCanvas.parentNode) {
        this.outputCanvas.parentNode.removeChild(this.outputCanvas)
      }
    }

    if (this.videoElement) {
      this.videoElement.classList.remove('video-zoom-video--active')
      this.videoElement.classList.add('video-zoom-video--idle')
    }

    // Stop only the canvas capture; in camera mode zoomedStream IS the live local stream.
    if (this.zoomedStream && this.zoomedStream !== this.originalStream) {
      this.zoomedStream.getTracks().forEach(track => track.stop())
    }

    // Reset state
    this.zoomLevel = 1.0
    this.isZoomed = false
    this.zoomCenter = { x: 0.5, y: 0.5 }
    this.zoomMode = null
    this.isProcessing = false
    this.isLocked = false
    this.pinchInitialZoom = null
    this.videoElement = null
    this.currentVideoTrack = null
    this.originalStream = null
    this.zoomedStream = null
  }
}

export default VideoZoom
